import { ICurrentUserStatus } from '@didomi/react';
import { CurrentUserStatus } from '@didomi/react-native';
import store from 'store';

import {
    selectConsentOrdering,
    selectDidomiUserStatus,
} from 'store/config/selectors';
import { StreamTypes } from 'store/player/actions.types';
import { RBProgram, RBTechnical } from 'store/railsBuilder/reducer.types';

import findCatchupTechnical from 'services/data-manager/catchup/findCatchupTechnical';
import { TECHNICAL_DEVICE_TYPE } from 'services/data-manager/constants';
import { VodEditorial } from 'services/data-manager/types';
import {
    UavContentTypes,
    UavEventPayload,
    UavEvents,
} from 'services/uav/types';
import { getUavAnalyticsUrl } from 'services/uav/uavEndpoints';

import NagraInstance from 'utils/axios/nagra/DefaultInstance';
import debugLog from 'utils/debugLog.ts';

export const MOBILE_PLAYBACK_CONTENT_SOURCE = 'OTT';

const SERVICE_OPERATION_EVENTS = [
    UavEvents.WATCH,
    UavEvents.PLAYBACK_START,
    UavEvents.PLAYBACK_STOP,
    UavEvents.PLAYBACK_PAUSE,
    UavEvents.PLAYER_ERROR,
    UavEvents.PLAYBACK_SKIP_AHEAD,
    UavEvents.PLAYBACK_SKIP_BACK,
    UavEvents.PLAYBACK_METRICS,
    UavEvents.PRIVACY_POLICY,
    UavEvents.PLAYBACK_RESUME,
    UavEvents.GENERIC_ERROR,
    UavEvents.SCHEDULE_RECORDING,
];
const SERVICE_PERSONALIZATION_EVENTS = [
    UavEvents.THIRD_PARTY_APP_START,
    UavEvents.RETURN_TO_LAUNCHER,
    UavEvents.DEEPLINK_TRIGGERED,
    UavEvents.APP_START,
    UavEvents.APP_END,
    UavEvents.APP_BACKGROUND,
    UavEvents.APP_FOREGROUND,
    UavEvents.AD_WATCHED,
    UavEvents.AD_DELIVERED,
    UavEvents.AD_SKIPED,
    UavEvents.RAIL_VIEW,
    UavEvents.RAIL_SELECTION,
];

export const logUavAnalytics = async (params?: UavEventPayload) => {
    try {
        if (params) {
            const consentOrdering = selectConsentOrdering(store.getState());
            const didomiUserStatus = selectDidomiUserStatus(store.getState());

            // Service operations events are of legitimate interest,
            // so they have to be sent in any case.
            let enabled = false;

            if (SERVICE_OPERATION_EVENTS.includes(params.name)) {
                enabled = true;
            } else if (SERVICE_PERSONALIZATION_EVENTS.includes(params.name)) {
                const personalizationConsentId = consentOrdering?.find((id) =>
                    id.startsWith('personaliz-'),
                );

                enabled = isConsentEnabled(
                    personalizationConsentId,
                    didomiUserStatus,
                );
            }

            if (enabled) {
                await NagraInstance.post(
                    getUavAnalyticsUrl(params.name),
                    params,
                );
            } else {
                debugLog(
                    `UAV event "${params.name}" is NOT enabled due to consent status.`,
                    params.metadata?.consentMask,
                );
            }
        }
    } catch (error: any) {
        if (__DEV__) {
            console.warn(
                '[logUavAnalytics error]: ',
                JSON.stringify(error, null, 2),
            );
        }
    }
};

export function getPlayBackType(
    playbackTypeInput?: StreamTypes,
    program?: RBProgram | VodEditorial,
): UavContentTypes | undefined {
    switch (playbackTypeInput) {
        case StreamTypes.live: {
            return UavContentTypes.LIVE;
        }
        case StreamTypes.startover: {
            return UavContentTypes.SO;
        }
        case StreamTypes.npvr: {
            return UavContentTypes.NPVR;
        }
        case StreamTypes.catchup: {
            const cuTechnical = findCatchupTechnical(
                (program as VodEditorial | undefined)?.technicals ||
                    (program as RBProgram | undefined)?.editorial?.technicals,
            );

            if (cuTechnical?.isSTCU) {
                return UavContentTypes.STCU;
            }

            if (cuTechnical?.isLTCU) {
                return UavContentTypes.LTCU;
            }

            return undefined;
        }
        case undefined: {
            return undefined;
        }
        default: {
            return UavContentTypes.VOD;
        }
    }
}

export function getTechnicalId(program?: RBProgram | VodEditorial) {
    if (!program) return undefined;
    const technicals =
        (program as RBProgram)?.editorial?.technicals ??
        (program as VodEditorial)?.technicals;
    // @ts-ignore
    const selectedTechnical = technicals?.find((technical: RBTechnical) =>
        technical.deviceType?.includes(TECHNICAL_DEVICE_TYPE),
    );
    return selectedTechnical?.id;
}

function isConsentEnabled(
    consentId: string | undefined,
    didomiUserStatus: CurrentUserStatus | ICurrentUserStatus | undefined,
) {
    const consentStatus = consentId
        ? didomiUserStatus?.purposes?.[consentId]
        : undefined;

    return !!consentStatus?.enabled;
}
